import { CredentialType } from "@jnctn/credentials/api/responses";
import {
  FetchNotFoundRequest,
  FetchUnauthorizedRequest,
} from "@jnctn/error-handling/Errors";
import { isChecksumValid } from "@jnctn/utilities/isChecksumValid";
import handleNotOkResponse from "@jnctn/error-handling/handleNotOkResponse";

export type MemberSubject = {
  id: string;
  firstName: string;
  lastName: string;
  image: string;
  name: string;
  email: string;
};

export type Member = {
  "@context": Array<string>;
  id: string;
  type: Array<CredentialType>;
  issuer: { id: string; name: string };
  expirationDate: string;
  credentialSubject: MemberSubject;
  credentialSchema: {
    id: string;
    type: "JsonSchemaValidator2018";
  };
  credentialStatus: {
    currentStatus: "active" | "inactive";
    type: "JnctnProfileStatus2020";
    id: string;
  };
};

export type MemberResult = {
  query: {
    interface: "collections";
    type: "profile";
    credentialSubjectId?: Array<string>;
  };
  orders: Array<any>;
  pages: Array<Member>;
  skip: number;
  top: number;
  totalRows: number;
};

export function getMemberFilterTotalRows(result: MemberResult): number {
  return result.totalRows;
}

export async function fetchMember(
  accessToken: string | undefined,
  tenantId: string,
  checksum: string
) {
  if (accessToken === undefined) {
    throw new FetchUnauthorizedRequest(fetchMember, "Unauthorized");
  }

  const res = await fetch(
    `${process.env.REACT_APP_QUERY_API_URL}/app/collection/query/`,
    {
      method: "POST",
      credentials: "include",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "JNCTN-TENANT-ID": tenantId,
      },
      body: JSON.stringify({
        skip: 0,
        top: 1,
        query: {
          interface: "collections",
          type: "profile",
          credentialSubjectId: [checksum],
        },
        orders: null,
      }),
    }
  );

  if (!res.ok) {
    handleNotOkResponse(res, fetchMember);
  }

  const memberResult: MemberResult = await res.json();

  // Simulate a not found error when a single member is requested but not found
  if (memberResult.pages.length !== 1) {
    throw new FetchNotFoundRequest(
      fetchMember,
      `Member not found '${checksum}'`
    );
  }

  return memberResult;
}

export default async function fetchMemberFilter(
  skip: number,
  top: number,
  accessToken: string | undefined,
  tenantId: string,
  {
    search,
    ids,
  }: {
    search?: string;
    ids?: Set<string>;
  }
): Promise<MemberResult> {
  if (accessToken === undefined) {
    throw new FetchUnauthorizedRequest(fetchMemberFilter, "Unauthorized");
  }

  if (search === undefined && ids !== undefined && ids.size === 0) {
    return {
      query: {
        interface: "collections",
        type: "profile",
      },
      orders: [],
      pages: [],
      skip,
      top,
      totalRows: 0,
    };
  }

  if (search !== undefined && isChecksumValid(search)) {
    ids = new Set([search]);
    search = undefined;
  }

  const res = await fetch(
    `${process.env.REACT_APP_QUERY_API_URL}/app/collection/query/`,
    {
      method: "POST",
      credentials: "include",
      mode: "cors",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "JNCTN-TENANT-ID": tenantId,
      },
      body: JSON.stringify({
        skip,
        top,
        query: {
          interface: "collections",
          type: "profile",
          credentialSubjectId: ids !== undefined ? Array.from(ids) : undefined,
          search,
        },
        orders: null,
      }),
    }
  );

  if (!res.ok) {
    handleNotOkResponse(res, fetchMemberFilter);
  }

  return (await res.json()) as MemberResult;
}
